import { z } from 'zod'

/**
 * The city a location is in (a WorldLocations id): it is what gives the location its coordinates
 * and time zone, so it is mandatory for a city location when it is created or updated (Andrew,
 * 2026-10-01). A location can instead be a country or a geographic region, which has no
 * coordinates (Andrew, 2026-10-19): a country needs only the country, a region only its name.
 * The server refuses the same saves; this is so the form says so before the save. The messages are
 * i18n keys - the form translates them.
 */
export const WORLD_LOCATION_REQUIRED_KEY = 'locations.worldLocationRequired'
export const COUNTRY_REQUIRED_KEY = 'locations.countryRequired'
const isWorldLocation = (v: unknown) => typeof v === 'number' && Number.isInteger(v) && v > 0
const kindRules = (data: { scope?: string; locationId?: number | null; countryCode?: string | null }, ctx: z.RefinementCtx) => {
    const scope = data.scope ?? 'city'
    if (scope === 'city' && !isWorldLocation(data.locationId)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['locationId'], message: WORLD_LOCATION_REQUIRED_KEY })
    if (scope === 'country' && !/^[A-Z]{2}$/.test(data.countryCode ?? '')) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['countryCode'], message: COUNTRY_REQUIRED_KEY })
}

export const locationSchema = z.object({
    id: z.number().optional(),
    description: z.string().min(1, 'Description is required'),
    address1: z.string().optional(),
    address2: z.string().optional(),
    county: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    category: z.string().optional(),
    categoryId: z.number().nullable().optional(),
    locationId: z.number().nullable().optional(),
    imageFilename: z.string().nullable().optional(),
    scope: z.enum(['city', 'country', 'region']).optional(),
    countryCode: z.string().nullable().optional(),
})

export const createLocationSchema = locationSchema.omit({ id: true }).superRefine(kindRules)

// Not optional on an update either: a city location saved without a city is the thing being stopped.
export const updateLocationSchema = locationSchema.partial().extend({
    description: z.string().min(1, 'Description is required').optional(),
}).superRefine(kindRules)

export type LocationFormData = z.infer<typeof locationSchema>

